import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Lineup', path: '/lineup' },
  { name: 'Gallery', path: '/gallery' },
  {
    name: 'Event Details',
    dropdown: [
      { name: 'Local Area', path: '/event-details/local-area' },
      { name: 'How To Get There', path: '/event-details/how-to-get-there' },
      { name: 'Gate & Camping', path: '/event-details/gate-camping' },
      { name: 'What To Bring', path: '/event-details/what-to-bring' },
      { name: 'Sustainability', path: '/event-details/sustainability' },
      { name: 'Services & Safety', path: '/event-details/services-safety' },
      { name: 'Rules & Terms', path: '/event-details/rules-terms' },
    ],
  },
  {
    name: 'Applications',
    dropdown: [
      { name: 'Artists', path: '/applications/musicians' },
      { name: 'Volunteers', path: '/applications/volunteers' },
    ],
  },
  { name: 'FAQ & Contact', path: '/faq-contact' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-ff-blue/95 backdrop-blur-md py-3 shadow-lg' 
            : 'bg-gradient-to-b from-ff-blue/90 to-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo - Text only */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <span className="font-supertaste text-2xl sm:text-3xl text-ff-yellow drop-shadow-[2px_2px_0px_#A63902]">
              FREQUENCY FOREST
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <button
                    className="px-4 py-2 text-sm font-semibold text-ff-brown/80 hover:text-ff-brown transition-colors"
                    data-testid={`nav-${link.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {link.name}
                    <ChevronDown className={`w-3 h-3 inline ml-1 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-4 py-2 text-sm font-semibold transition-colors ${
                      link.highlight
                        ? 'text-ff-brown'
                        : 'text-ff-brown/80 hover:text-ff-brown'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )}

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {link.dropdown && activeDropdown === link.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-md rounded-2xl overflow-hidden shadow-xl border-2 border-ff-outline/20"
                    >
                      {link.dropdown.map((subLink) => (
                        <Link
                          key={subLink.path}
                          to={subLink.path}
                          className="block px-4 py-3 text-sm text-ff-brown/80 hover:bg-ff-yellow/20 hover:text-ff-brown transition-colors font-medium"
                          data-testid={`nav-dropdown-${subLink.name.toLowerCase().replace(/ /g, '-')}`}
                        >
                          {subLink.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* CTA Button */}
            <Link
              to="/lineup"
              className="ml-4 bg-ff-yellow text-ff-brown px-6 py-2.5 rounded-full text-sm font-bold hover:brightness-105 transition-all"
              data-testid="nav-lineup-btn"
            >
              Lineup
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-ff-brown p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-ff-green pt-20"
            data-testid="mobile-menu"
          >
            <div className="p-6 space-y-1 h-full overflow-y-auto pb-32">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <div>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)}
                        className="w-full flex items-center justify-between py-4 text-lg text-ff-brown font-semibold border-b border-ff-outline/20"
                      >
                        {link.name}
                        <ChevronDown className={`w-5 h-5 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === link.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden bg-white/30"
                          >
                            {link.dropdown.map((subLink) => (
                              <Link
                                key={subLink.path}
                                to={subLink.path}
                                className="block px-4 py-3 text-sm text-ff-brown/80 hover:text-ff-brown font-medium"
                              >
                                {subLink.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className="block py-4 text-lg font-semibold border-b border-ff-outline/20 text-ff-brown"
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}

              <Link
                to="/lineup"
                className="block w-full bg-ff-yellow text-ff-brown text-center py-4 rounded-full font-bold mt-8"
                data-testid="mobile-lineup-btn"
              >
                View Lineup
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
